import { Outlet, createRootRouteWithContext, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { CommandPalette } from '@/app/CommandPalette';
import { Cheatsheet } from '@/app/Cheatsheet';
import { ErrorState } from '@/ui/Panel';
import { errorMessage } from '@/lib/api/client';
import { resolveLegacyPath } from '@/lib/legacyRedirects';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  /**
   * Legacy URL rewrite — the client-side equivalent of the old admin's
   * `hooks.server.ts`, and deliberately in the same position: first, before
   * anything else in the chain, so an unauthenticated bookmark reaches the
   * canonical URL and *then* bounces to login rather than the reverse.
   *
   * It lives at the root, not only in the `/$` catch-all, because a few
   * retired URLs are captured by a live route before the splat gets a look in:
   * `/products/departments` matches `/products/$id`, `/vendors/purchase-orders`
   * matches `/vendors/$id`. Root `beforeLoad` runs regardless of which route
   * won the match, so one table covers both cases.
   *
   * Retire with `src/lib/legacyRedirects.ts` — see the date on that module.
   */
  beforeLoad: ({ location }) => {
    const destination = resolveLegacyPath(location.pathname, location.searchStr);
    if (destination) throw redirect({ href: destination, replace: true });
  },
  component: RootLayout,
  errorComponent: ({ error, reset }) => (
    <div className="grid h-full place-items-center p-8">
      <ErrorState
        title="This screen hit an error"
        message={errorMessage(error)}
        onRetry={reset}
      />
    </div>
  ),
  notFoundComponent: () => (
    <div className="grid h-full place-items-center p-8">
      <ErrorState
        title="No such screen"
        message="Check the address, or press ⌘K to jump somewhere."
      />
    </div>
  ),
});

function RootLayout() {
  return (
    <>
      <Outlet />
      <CommandPalette />
      <Cheatsheet />
      <Toaster
        position="bottom-right"
        // Match the console's chrome exactly — a default-styled toast library
        // is the fastest way to make an app look assembled rather than designed.
        toastOptions={{
          unstyled: true,
          classNames: {
            toast:
              'flex w-full items-start gap-2.5 rounded border border-line-strong bg-surface-raised p-2.5 text-sm text-ink shadow-lg shadow-black/30',
            title: 'text-sm font-medium leading-tight',
            description: 'mt-0.5 text-xs text-ink-muted leading-relaxed',
            actionButton:
              'ml-auto shrink-0 rounded-sm border border-line bg-surface px-2 py-1 text-xs hover:bg-surface-hover',
            error: '!border-negative/50',
            success: '!border-positive/40',
          },
        }}
      />
    </>
  );
}
